import React, { ChangeEvent, useEffect, useState } from 'react'

import ColorPicker from 'components/basic/color-picker/ColorPicker'
import { T } from "services/lang/LanguageService";
import { HighlightRange } from 'components/dashboard/product-template-table/utils/interfaces'
import { Button } from 'primereact/button'
import { Fieldset } from 'primereact/fieldset'
import { InputNumber } from 'primereact/inputnumber'
import { InputSwitch } from 'primereact/inputswitch'
import { Tooltip } from 'react-tooltip'
import TimeSpanInputs from './TimeSpanInputs'

type OutputHighlightProps = {
  bgHighlights: HighlightRange[];
  onAttributeChange: (bgList: HighlightRange[]) => void;
  forTimeSpan?: boolean;
};
const OutputHighlight: React.FC<OutputHighlightProps> = ({
  bgHighlights,
  onAttributeChange,
  forTimeSpan = false,
}) => {
  const [bgHighlightList, setBgHighlightList] = useState<HighlightRange[]>(bgHighlights);

  useEffect(() => {
    onAttributeChange(bgHighlightList);
  }, [bgHighlightList]);

  const onBgHighlightRangeChange = (
    value: number | boolean | null,
    bgRange: HighlightRange,
    index: number,
    field: "min" | "max"
  ) => {
     if (value === null) return;
    const updatedBgRange = {
      ...bgRange,
      [field]:
        typeof value === "boolean"
          ? { ...bgRange[field], inclusive: value }
          : { ...bgRange[field], value },
    };

    setBgHighlightList(prev => prev.map((bgr, i) => (i === index ? updatedBgRange : bgr)));
  };

  const addBgHighlightRange = () =>
    setBgHighlightList(prev => [
      ...prev,
      {
        min: { value: 0, inclusive: false },
        max: { value: 0, inclusive: false },
        color: "#000000",
        bgColor: "#ffffff",
      },
    ]);

  const onColorChange = (
    bgRange: HighlightRange,
    i: number,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id: field } = event.target;
    const updBgRange = { ...bgRange, [field]: value };
    setBgHighlightList(prev => prev.map((bgr, index) => (index === i ? updBgRange : bgr)));
  };

  const mappedBgHighlightRanges = bgHighlightList.map((bgRange, i) => (
    <div
      className="flex flex-nowrap justify-content-between gap-4 mt-1"
      // eslint-disable-next-line react/no-array-index-key
      key={`bg-${i}`}
    >
      <div className="p-field flex-1">
        {i === 0 && <label htmlFor="min">{T("load-conf.min-value")}</label>}
        <div className="p-inputgroup">
          {!forTimeSpan && (
            <InputNumber
              id="min"
              value={bgRange.min?.value ?? 0}
              name="value"
              onChange={e => onBgHighlightRangeChange(e.value, bgRange, i, "min")}
              className="p-inputtext-sm"
              style={{ blockSize: "2.8rem" }}
            />
          )}
          {forTimeSpan && (
            <TimeSpanInputs value={bgRange.min?.value ?? 0} onChange={val => onBgHighlightRangeChange(val, bgRange, i, "min")}/>
          )}
          <div className="p-inputgroup-addon" style={{ blockSize: "2.8rem" }}>
            <Tooltip id="inclusiveMin" />
            <InputSwitch
              data-tooltip-id="inclusiveMin"
              data-tooltip-content={T("word.inclusive-min")}
              checked={bgRange.min?.inclusive ?? false}
              onChange={e => onBgHighlightRangeChange(e.value, bgRange, i, "min")}
              className="p-inputtext-sm"
            />
          </div>
        </div>
      </div>

      <div className="p-field flex-1">
        {i === 0 && <label htmlFor="max">{T("load-conf.max-value")}</label>}
        <div className="p-inputgroup">
          {!forTimeSpan && <InputNumber
            id="max"
            value={bgRange.max?.value ?? 0}
            onChange={e => onBgHighlightRangeChange(e.value, bgRange, i, "max")}
            className="p-inputtext-sm"
            style={{ blockSize: "2.8rem" }}
          />}
           {forTimeSpan && (
            <TimeSpanInputs value={bgRange.max?.value ?? 0} onChange={val => onBgHighlightRangeChange(val, bgRange, i, "max")}/>
          )}
          <div className="p-inputgroup-addon" style={{ blockSize: "2.8rem" }}>
            <Tooltip id="inclusiveMax" />
            <InputSwitch
              data-tooltip-content={T("word.inclusive-max")}
              data-tooltip-id="inclusiveMax"
              checked={bgRange.max?.inclusive ?? false}
              onChange={e => onBgHighlightRangeChange(e.value, bgRange, i, "max")}
            />
          </div>
        </div>
      </div>

      <div className="p-field">
        {i === 0 && (
          <label htmlFor="color" className="block">
            {T("word.text-color")}
          </label>
        )}
        <ColorPicker
          id="color"
          value={bgRange.color ?? "#000000"}
          onValueChange={e => onColorChange(bgRange, i, e)}
        />
      </div>
      <div className="p-field w-8rem">
        {i === 0 && (
          <label htmlFor="color" className="block">
            {T("word.background-color")}
          </label>
        )}
        <ColorPicker
          id="bgColor"
          value={bgRange.bgColor}
          onValueChange={e => onColorChange(bgRange, i, e)}
        />
      </div>
      <div className="p-field align-self-end">
        <Tooltip id={`remove-${i}-btn`} />
        <Button
          data-tooltip-id={`remove-${i}-btn`}
          data-tooltip-content={T("action.remove-row")}
          icon="pi pi-trash"
          onClick={() => setBgHighlightList(prev => prev.toSpliced(i, 1))}
          className="p-button-outlined p-button-danger"
        />
      </div>
    </div>
  ));

  return (
    <Fieldset legend={T("word.output-highlight")} className="p-col-12" toggleable>
      {mappedBgHighlightRanges}

      <div className="p-field flex-1">
        <Tooltip id="add-highlight" />
        <Button
          data-tooltip-id="add-highlight"
          data-tooltip-content={T("action.add-new-highlight-row")}
          data-tooltip-place="right"
          icon="pi pi-plus"
          onClick={addBgHighlightRange}
          className="p-button-outlined p-button-success p-button-rounded"
        />
      </div>
    </Fieldset>
  );
};

export default OutputHighlight;